import { Alert, Box, Button, Flex, Modal, Skeleton, Text } from "@mantine/core";
import { StatusBadge } from "../../components/ui/status-badge";
import { Page, PageHeader } from "../../components/layout/Page";
import { CheckCircleIcon, WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr, { useSWRConfig } from "swr";
import { LedgerApi } from "../../apis";
import { usePermission } from "../../components/permission";
import { ILedgerConfig, LedgerConfigMode } from "../../apis/ledger";
import { Notice } from "../../components/ui/notice";
import { message } from "../../utils/Message";
import { dateTimeShortFormat } from "../../utils/date";
import journal from "./journal.module.css";
import classes from "./settings.module.css";

/**
 * Дансны дэвтрийн тохиргоо (SPRINT-09 Алхам 1).
 *
 * Энэ бол дэвтэр УНТРААЛТТАЙ үед харагддаг цорын ганц санхүүгийн дэлгэц.
 * Бусад 14 нь `ledgerOnly` тул `business_ledger_config.mode === "INTERNAL"`
 * болтол цэсэнд огт гарахгүй — өөрөөр хэлбэл энэ дэлгэцгүйгээр дэвтрийг
 * хаанаас ч асаах боломжгүй байсан: `setLedgerMode`-ыг зөвхөн драйлын
 * скриптүүд дуудаж байв.
 *
 * `supplierOnly` БИШ, зориудаар: MEMO данс зөвхөн эзэмшигч талд үүсдэг бөгөөд
 * DeHUB-д тэр нь BUYER төрлийн бизнес. Нийлүүлэгч талд хаавал консигнацийн
 * хосын нөгөө хагас нь дэвтрээ хэзээ ч асааж чадахгүй болно.
 */
export const LedgerSettingsPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const canFinance = usePermission("FINANCE").hasAccess;

  const { data, isLoading, error, mutate } = useSwr<ILedgerConfig>(
    "ledger.config",
    async () => await LedgerApi.getLedgerConfig(),
    { revalidateOnFocus: false },
  );

  // Layout gates finance nav on the same "ledger.config" key
  // (components/layout/index.tsx). Sharing the key means one fetch + mutate
  // refreshes both this page and the sidebar gate.
  const { mutate: mutateGlobal } = useSWRConfig();

  // Ганц эх сурвалж: аль баталгаажуулах модал нээлттэй байгаа. Хоёр тусдаа
  // boolean нь хоёуланг нь нээлттэй болгож болзошгүй төлөв үүсгэнэ.
  const [confirm, setConfirm] = useState<LedgerConfigMode | null>(null);
  const [busy, setBusy] = useState(false);

  // Аль аль нь props/server-ээс гаралтай — useState-д хуулбарлахгүй.
  const mode = data?.mode ?? "OFF";
  const isOn = mode === "INTERNAL";

  const apply = async (next: LedgerConfigMode) => {
    setBusy(true);
    try {
      const result = await LedgerApi.setLedgerConfig(next);
      // Хариу нь GET-ийн бүтэц дээр accountsEnsured нэмсэн superset тул
      // кэшийг дахин уншилгүйгээр шууд солиж болно.
      await mutate(result, { revalidate: false });
      await mutateGlobal("ledger.config");
      message.success(
        next === "INTERNAL"
          ? t("billing:ledger.config.enabledToast", { count: result.accountsEnsured })
          : t("billing:ledger.config.disabledToast"),
      );
      setConfirm(null);
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:ledger.config.title")}
            <Text span className={journal.titleChip}>
              {t(isOn ? "billing:ledger.config.stateOn" : "billing:ledger.config.stateOff")}
            </Text>
          </>
        }
        subtitle={t("billing:ledger.config.subtitle")}
      />

      {error && (
        <Notice tone="danger" role="alert" title={t("billing:ledger.report.loadError")} />
      )}

      <Box className={classes.card}>
        {isLoading ? (
          <Box p="md">
            <Skeleton h={14} mb="sm" />
            <Skeleton h={14} w="60%" />
          </Box>
        ) : (
          <>
            <Box className={classes.head}>
              <Box className={classes.heading}>
                <Text span className={classes.title}>{t("billing:ledger.config.ledgerLabel")}</Text>
                <Text span className={classes.sub}>{t("billing:ledger.config.ledgerDesc")}</Text>
              </Box>
              <StatusBadge
                color={isOn ? "green" : "gray"}
                label={t(isOn ? "billing:ledger.config.stateOn" : "billing:ledger.config.stateOff")}
              />
            </Box>

            <dl className={classes.facts}>
              <Box className={classes.fact}>
                <dt>{t("billing:ledger.config.colEnabledAt")}</dt>
                <dd>{data?.enabledAt ? dateTimeShortFormat(data.enabledAt) : "-"}</dd>
              </Box>
              <Box className={classes.fact}>
                <dt>{t("billing:ledger.config.colDisabledAt")}</dt>
                <dd>{data?.disabledAt ? dateTimeShortFormat(data.disabledAt) : "-"}</dd>
              </Box>
            </dl>

            {isOn ? (
              <Box className={classes.danger}>
                <Box className={classes.heading}>
                  <Text span className={classes.dangerTitle}>{t("billing:ledger.config.disableButton")}</Text>
                  <Text span className={classes.sub}>{t("billing:ledger.config.disableDesc")}</Text>
                </Box>
                {canFinance && (
                  <Button
                    variant="default"
                    className={classes.dangerBtn}
                    onClick={() => setConfirm("OFF")}>
                    {t("billing:ledger.config.disableAction")}
                  </Button>
                )}
              </Box>
            ) : (
              <Flex justify="flex-end" className={classes.foot}>
                {canFinance && (
                  <Button onClick={() => setConfirm("INTERNAL")}>
                    {t("billing:ledger.config.enableButton")}
                  </Button>
                )}
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* Асаах баталгаажуулалт — өнгө утгатай Alert + хураангуй карт, апп даяар
          хэрэглэгддэг хэв маяг (periods.tsx, journal.tsx). */}
      <Modal
        opened={confirm === "INTERNAL"}
        onClose={() => setConfirm(null)}
        title={t("billing:ledger.config.enableConfirmTitle")}
        size="sm">
        <Box>
          <Alert color="green" radius="md" icon={<CheckCircleIcon size={18} weight="bold" />} mb="md">
            {t("billing:ledger.config.enableConfirmBody")}
          </Alert>
          <Alert color="yellow" radius="md" icon={<WarningIcon size={18} weight="bold" />} mb="md">
            {t("billing:ledger.config.enableConfirmWarning")}
          </Alert>
          <Flex justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" onClick={() => setConfirm(null)} disabled={busy}>
              {t("common:cancel")}
            </Button>
            <Button loading={busy} onClick={() => apply("INTERNAL")}>
              {t("billing:ledger.config.enableButton")}
            </Button>
          </Flex>
        </Box>
      </Modal>

      {/* Унтраах баталгаажуулалт — данс, бичилт УСТАХГҮЙ гэдгийг ил хэлнэ. */}
      <Modal
        opened={confirm === "OFF"}
        onClose={() => setConfirm(null)}
        title={t("billing:ledger.config.disableConfirmTitle")}
        size="sm">
        <Box>
          <Alert color="red" radius="md" icon={<WarningIcon size={18} weight="bold" />} mb="md">
            {t("billing:ledger.config.disableConfirmBody")}
          </Alert>
          <Flex justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" onClick={() => setConfirm(null)} disabled={busy}>
              {t("common:cancel")}
            </Button>
            <Button color="red" loading={busy} onClick={() => apply("OFF")}>
              {t("billing:ledger.config.disableButton")}
            </Button>
          </Flex>
        </Box>
      </Modal>
    </Page>
  );
};

export default LedgerSettingsPage;
